import { useState } from 'react';
import { useAuctions, useAuctionPeriod, useAuctionParams } from '@/hooks/useAuctions';
import { useWallet } from '@/hooks/useWallet';
import { AuctionList } from './AuctionList';
import { BidModal } from './BidModal';
import type { Auction } from '@/types/auctions';
import { ConnectWalletPrompt } from '@/components/Wallet';
import { text } from '@/config/theme';

export function Auctions(): JSX.Element {
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const { data: auctions, isLoading: auctionsLoading, refetch } = useAuctions();
  const { data: period, isLoading: periodLoading } = useAuctionPeriod();
  const { data: params } = useAuctionParams();
  const { isConnected } = useWallet();

  const handleBid = (auction: Auction): void => {
    setSelectedAuction(auction);
  };

  const handleCloseModal = (): void => {
    setSelectedAuction(null);
  };

  const handleBidSuccess = (): void => {
    // Refetch auctions to get updated highest bid
    void refetch();
  };

  const isLoading = auctionsLoading || periodLoading;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-3xl font-bold mb-2 ${text.primary}`}>Fee Auctions</h1>
        <p className={`${text.secondary} mb-4`}>
          Bid on Gravity Bridge fee auctions to earn token rewards
        </p>
      </div>

      {/* Auction Info Bar */}
      {period !== undefined && period !== null && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-purple-900 mb-1">
                Current Auction Period
              </h3>
              <div className="flex gap-6 text-sm text-purple-700">
                <div>
                  <span className="font-medium">Time Remaining:</span> {period.remaining_time}
                </div>
                <div>
                  <span className="font-medium">Blocks:</span> {period.remaining_blocks}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-600">
              <svg
                className="h-5 w-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Auto-refreshing every 10s</span>
            </div>
          </div>
        </div>
      )}

      {/* Min Bid Fee Info */}
      {params !== undefined && params !== null && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">Minimum Bid Fee: {(parseFloat(params.min_bid_fee) / 1e6).toFixed(6)} GRAV</p>
              <p className="text-yellow-700 text-sm mt-1">
                This non-refundable fee is required for every bid. 
                {params.burn_winning_bids && ' Winning bids are burned.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection Prompt */}
      {!isConnected && (
        <div className="mb-6">
          <ConnectWalletPrompt actionDescription="place bids on auctions" />
        </div>
      )}

      {/* Auction List */}
      <AuctionList
        auctions={auctions ?? []}
        onBid={handleBid}
        isLoading={isLoading}
      />

      {/* Bid Modal */}
      {selectedAuction !== null && selectedAuction !== undefined && params !== null && params !== undefined && (
        <BidModal
          auction={selectedAuction}
          minBidFee={params.min_bid_fee}
          onClose={handleCloseModal}
          onSuccess={handleBidSuccess}
        />
      )}
    </div>
  );
}
